import { useState } from 'react';
import emailjs from 'emailjs-com';
import { toast } from 'react-toastify';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClock, FaFacebookF, FaInstagram, FaLinkedinIn, FaYoutube } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { Send } from 'lucide-react';

const infoItems = [
  {
    icon: FaMapMarkerAlt,
    label: 'Kigali Office',
    value: 'KK 04 Ave - Gikondo, Kigali, Rwanda',
  },
  {
    icon: FaMapMarkerAlt,
    label: 'Nyamata Office',
    value: 'Providence House, Room 216 — Nyamata',
  },
  {
    icon: FaPhoneAlt,
    label: 'Phone',
    value: '+250 788 570 601',
    href: 'tel:+250788570601',
  },
  {
    icon: FaEnvelope,
    label: 'Email',
    value: 'info@edgesdesigngroup.com',
    href: 'mailto:info@edgesdesigngroup.com',
  },
  {
    icon: FaClock,
    label: 'Working Hours',
    value: 'Mon – Fri: 8:00 AM – 5:00 PM',
  },
];

const socialLinks = [
  { label: 'Facebook', icon: FaFacebookF, href: 'https://www.facebook.com/profile.php?id=61559936103250', color: 'hover:bg-blue-600' },
  { label: 'Instagram', icon: FaInstagram, href: 'https://www.instagram.com/edges.design.group/', color: 'hover:bg-pink-600' },
  { label: 'LinkedIn', icon: FaLinkedinIn, href: 'https://www.linkedin.com/in/edges-design-group-06857a364/?skipRedirect=true', color: 'hover:bg-sky-600' },
  { label: 'YouTube', icon: FaYoutube, href: 'https://www.youtube.com/@Edges_Design_Group', color: 'hover:bg-red-500' },
  { label: 'X', icon: FaXTwitter, href: 'https://x.com/Edges_Design', color: 'hover:bg-gray-700' },
];

function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendEmail = (e) => {
    e.preventDefault();
    setSending(true);

    emailjs.send(
      'service_q2kk0pg',
      'template_rj8yup3',
      formData,
      'Otoba8htXPl0-qcwp'
    )
    .then(() => {
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    })
    .catch(() => {
      toast.error('Failed to send message. Please try again.');
    })
    .finally(() => setSending(false));
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Section heading */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-teal-500 shadow-[0_0_8px_3px_rgba(20,184,166,0.7)]" />
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shadow-[0_0_6px_2px_rgba(20,184,166,0.5)]" />
              <span className="w-1 h-1 rounded-full bg-teal-300 shadow-[0_0_4px_1px_rgba(20,184,166,0.3)]" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-widest text-teal-600">Reach Out</span>
            <span className="flex items-center gap-1 scale-x-[-1]">
              <span className="w-2 h-2 rounded-full bg-teal-500 shadow-[0_0_8px_3px_rgba(20,184,166,0.7)]" />
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shadow-[0_0_6px_2px_rgba(20,184,166,0.5)]" />
              <span className="w-1 h-1 rounded-full bg-teal-300 shadow-[0_0_4px_1px_rgba(20,184,166,0.3)]" />
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">Get In Touch</h2>
          <p className="mt-3 text-gray-500 max-w-xl mx-auto">
            Have a project in mind or need a consultation? We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

          {/* LEFT — Info */}
          <div className="lg:col-span-2 flex flex-col gap-6">

            {/* Info items */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex flex-col gap-6">
              {infoItems.map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center shrink-0">
                    <Icon className="text-teal-600" size={16} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-0.5">{label}</p>
                    {href ? (
                      <a href={href} className="text-gray-800 text-sm hover:text-teal-600 transition">{value}</a>
                    ) : (
                      <p className="text-gray-800 text-sm">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social links */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Follow Us</p>
              <div className="flex gap-3 flex-wrap">
                {socialLinks.map(({ label, icon: Icon, href, color }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className={`w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-all duration-200 ${color} hover:text-white hover:border-transparent hover:scale-110`}
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>

            {/* Map embed */}
            <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100 h-48">
              <iframe
                title="Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3987.5!2d30.0619!3d-1.9706!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMcKwNTgnMTQuMiJTIDMwwrAzJzQyLjgiRQ!5e0!3m2!1sen!2srw!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* RIGHT — Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10 h-full">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
              <form className="flex flex-col gap-5" onSubmit={sendEmail}>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="px-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                      className="px-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    placeholder="What's this about?"
                    className="px-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    placeholder="Tell us about your project..."
                    required
                    className="px-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-150 self-start"
                >
                  <Send size={16} />
                  {sending ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

export default Contact;
